import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormLabel,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import { updateDriverById } from "../slices/authSlice";
import {
  showNotification,
  NotificationType,
} from "../slices/notificationSlice";
import { useNavigate, Navigate } from "react-router-dom";
import * as consts from "../constants";
import { parseAddress, assembleAddress } from "../utils/addressUtils";

const DriverInfo = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const driverInfo = useAppSelector((state) => state.auth.driverInfo);

  useEffect(() => {
    if (driverInfo) {
      setName(driverInfo.name || "");
      setPhone(driverInfo.phone || "");
      setEmail(driverInfo.email || "");
      setRestDay(
        driverInfo.dayoff !== null && driverInfo.dayoff !== undefined
          ? driverInfo.dayoff
          : -1
      );

      if (driverInfo.homeaddr) {
        const addrParts = parseAddress(driverInfo.homeaddr);
        setAddrStreet(addrParts.street);
        setAddrZipCode(addrParts.zipcode);
        setAddrCity(addrParts.city);
      }
    }
  }, [driverInfo]);

  const handleBack = async () => {
    navigate("/driver-select");
  };

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [addrStreet, setAddrStreet] = useState("");
  const [addrZipCode, setAddrZipCode] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [restDay, setRestDay] = useState<number>(-1);

  const [phoneError, setPhoneError] = useState("");
  const [addrStreetError, setAddrStreetError] = useState("");
  const [addrCityError, setAddrCityError] = useState("");
  const [addrZipCodeError, setAddrZipCodeError] = useState("");

  if (!driverInfo) {
    return <Navigate replace to={"/driver-login"} />;
  }

  const handleUpdate = async () => {
    setPhoneError("");

    if (!phone) {
      setPhoneError(t("errPhoneRequired"));
      return;
    }

    // 添加地址验证
    if (!validateAddr()) {
      return;
    }

    const addr = assembleAddress({
      street: addrStreet,
      zipcode: addrZipCode,
      city: addrCity,
      state: consts.defaultState,
    });

    const data: any = {
      user_id: +driverInfo.uid,
      phone,
      dayoff: restDay,
    };

    if (addr) {
      data.homeaddr = addr;
    }

    // console.log("data: ", data); // 打印data，用于调试

    try {
      await dispatch(updateDriverById(data)).unwrap();
      dispatch(
        showNotification({
          message: t("userInfoUpdateSuccess"),
          type: NotificationType.Success,
        })
      );
      // 修改成功后返回上一页
      navigate(-1);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.message || t("userInfoUpdateFailed"),
          type: NotificationType.Error,
        })
      );
    }
  };

  // 验证地址的必填字段
  const validateAddr = () => {
    setAddrStreetError("");
    setAddrCityError("");
    setAddrZipCodeError("");

    const hasStreet = !!addrStreet;
    const hasCity = !!addrCity;
    const hasZipCode = !!addrZipCode;

    // 如果有任意一个字段填写，则其他字段都必须填写
    if (hasStreet || hasCity || hasZipCode) {
      if (!hasStreet) {
        setAddrStreetError(t("errStreetRequired"));
      }
      if (!hasCity) {
        setAddrCityError(t("errCityRequired"));
      }
      if (!hasZipCode) {
        setAddrZipCodeError(t("errZipCodeRequired"));
      }
      return hasStreet && hasCity && hasZipCode;
    }
    return true; // 全部为空也是合法的
  };

  const weekdays = [
    { value: -1, label: t("noRestDays") },
    { value: 0, label: t("sunday") },
    { value: 1, label: t("monday") },
    { value: 2, label: t("tuesday") },
    { value: 3, label: t("wednesday") },
    { value: 4, label: t("thursday") },
    { value: 5, label: t("friday") },
    { value: 6, label: t("saturday") },
  ];

  return (
    <>
      <Container maxWidth="sm">
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{t("selectMyInfo")}</Typography>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  required
                  fullWidth
                  id="name"
                  label={t("name")}
                  value={name}
                  disabled={true} // 姓名不可修改
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="email"
                  label={t("emailAddress")}
                  value={email}
                  disabled={true} // email不可修改
                  //   onChange={(e) => setEmail(e.target.value)}
                  //   error={
                  //     !!emailError ||
                  //     (email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
                  //   }
                  //   helperText={
                  //     emailError ||
                  //     (email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                  //       ? t("errEmailInvalid")
                  //       : "")
                  //   }
                  //   placeholder="example@email.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="phone"
                  label={t("phone")}
                  value={phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value || /^\+?[0-9]{0,15}$/.test(value)) {
                      setPhone(value);
                    }
                  }}
                  error={
                    !!phoneError ||
                    (phone !== "" && !/^\+?[0-9]{10,15}$/.test(phone))
                  }
                  helperText={
                    phoneError ||
                    (phone !== "" && !/^\+?[0-9]{10,15}$/.test(phone)
                      ? t("errPhoneInvalid")
                      : "")
                  }
                  placeholder="+1234567890"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {t("address")}
                </Typography>
                <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="addr_street"
                        label={t("streetAddress")}
                        value={addrStreet}
                        onChange={(e) => setAddrStreet(e.target.value)}
                        error={!!addrStreetError}
                        helperText={addrStreetError}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        name="addr_city"
                        label={t("city")}
                        value={addrCity}
                        onChange={(e) => setAddrCity(e.target.value)}
                        error={!!addrCityError}
                        helperText={addrCityError}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        name="addr_zipcode"
                        label={t("zipcode")}
                        value={addrZipCode}
                        onChange={(e) => setAddrZipCode(e.target.value)}
                        error={!!addrZipCodeError}
                        helperText={addrZipCodeError}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormLabel id="rest-day-label">{t("labelRestDay")}</FormLabel>
                <Select
                  fullWidth
                  value={restDay}
                  onChange={(e) => setRestDay(Number(e.target.value))}
                  labelId="rest-day-label"
                >
                  {weekdays.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              onClick={handleUpdate}
            >
              {t("btnUpdate")}
            </Button>
            <Button fullWidth variant="contained" onClick={handleBack}>
              {t("btnBack")}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default DriverInfo;
